import { message } from "antd";
import { useEffect, useMemo } from "react";

import { patchOrganization, useGetOrganizationById } from "../../../api/v1";

import {
  getCatalogueDef,
  getValueByPath,
  mergeWithDefaultViewingDays,
  setValueByPath,
  stripUid,
} from "./marketingConfigurationHelpers";
import { CatalogueId, MarketingConfigurationItem, ViewingDayItem } from "./types";

function getCatalogueData(node: unknown): unknown[] {
  if (Array.isArray(node)) return node;
  if (node && typeof node === "object" && "data" in node) {
    const { data } = node as { data: unknown };
    if (Array.isArray(data)) return data;
  }
  return [];
}

function mapCatalogueItems(items: unknown[]): MarketingConfigurationItem[] {
  return items.map((item) => {
    const row = item as MarketingConfigurationItem & { uid?: string };
    return {
      ...row,
      uid: row.uid ?? crypto.randomUUID(),
    };
  }) as MarketingConfigurationItem[];
}

export function useMarketingConfigurationCatalogue(orgId: string | null, catalogueId: CatalogueId) {
  const { filterKey } = getCatalogueDef(catalogueId);

  const { data, isLoadingOrganization, isFetchingOrganization, isErrorOrganization, refetchOrganization } =
    useGetOrganizationById({
      orgId,
      params: { filterkeys: filterKey },
    });

  const items = useMemo<MarketingConfigurationItem[]>(() => {
    if (catalogueId === "marketingPublicListUrl") return [];
    if (!data?.success || !data.result) return [];

    const catalogueNode = getValueByPath(data.result, filterKey);
    const fetchedItems = mapCatalogueItems(getCatalogueData(catalogueNode));

    if (catalogueId !== "viewingDaysConfig") return fetchedItems;

    return mergeWithDefaultViewingDays(fetchedItems as ViewingDayItem[]);
  }, [catalogueId, data, filterKey]);

  const marketingPublicListUrl = useMemo(() => {
    if (catalogueId !== "marketingPublicListUrl" || !data?.success || !data.result) return null;

    const value = getValueByPath(data.result, filterKey);
    return typeof value === "string" ? value : null;
  }, [catalogueId, data, filterKey]);

  useEffect(() => {
    if (isErrorOrganization) {
      message.error("Something went wrong while fetching marketing data");
      return;
    }

    if (data && !data.success) {
      message.error(data.message ?? "Failed to fetch marketing data");
    }
  }, [data, isErrorOrganization]);

  useEffect(() => {
    if (!orgId || catalogueId !== "viewingDaysConfig" || !data?.success || !data.result) return;

    const catalogueNode = getValueByPath(data.result, filterKey);
    const fetchedItems = mapCatalogueItems(getCatalogueData(catalogueNode));
    const mergedItems = mergeWithDefaultViewingDays(fetchedItems as ViewingDayItem[]);

    if (mergedItems.length === fetchedItems.length) return;

    void (async () => {
      const patchResponse = await patchOrganization({
        orgId,
        data: setValueByPath(filterKey, mergedItems.map(stripUid)),
      });

      if (!patchResponse?.success) {
        message.error(patchResponse?.message ?? "Unable to save default viewing days");
      }
    })();
  }, [catalogueId, data, filterKey, orgId]);

  return {
    items,
    marketingPublicListUrl,
    isLoading: isLoadingOrganization || isFetchingOrganization,
    refetch: async () => {
      await refetchOrganization();
    },
  };
}
